import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { IconAlertSquareRounded, IconChevronLeft, IconEye, IconEyeOff } from '@tabler/icons-react';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import PropTypes from 'prop-types';
import PasswordRequirements from '../../PasswordRequirements/PasswordRequirements';

// Import your assets
import logoWhite from '../../../assets/images/Logo.png';

const ConfirmResetSchema = Yup.object().shape({
  code: Yup.string()
    .matches(/^\d+$/, 'Verification code must only contain numbers')
    .length(6, 'Verification code must be 6 digits')
    .required('Verification code is required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
    )
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your password'),
  showPassword: Yup.boolean(),
});

function ConfirmResetPassword({ username, onBack }) {
  const navigate = useNavigate();
  const [resendCooldown, setResendCooldown] = useState(0);
  const formikRef = useRef();

  const handleConfirmation = async (values, { setSubmitting, setStatus }) => {
    try {
      await confirmResetPassword({
        username,
        confirmationCode: values.code,
        newPassword: values.newPassword,
      });

      // Show success message and redirect to login
      setStatus({
        success: 'Password reset successful! Redirecting to login...',
      });
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      let errorMessage = 'An unexpected error occurred. Please try again.';

      switch (err.name) {
        case 'CodeMismatchException':
          errorMessage = 'Invalid verification code. Please try again.';
          break;
        case 'ExpiredCodeException':
          errorMessage =
            'Verification code has expired. Please request a new one.';
          break;
        case 'LimitExceededException':
          errorMessage = 'Too many attempts. Please try again later.';
          break;
        default:
          errorMessage = 'Failed to reset password. Please try again.';
      }

      setStatus({ error: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await resetPassword({ username });

      // Start cooldown
      setResendCooldown(30);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Show success message using Formik's setStatus
      if (formikRef.current) {
        formikRef.current.setStatus({
          success: 'A new verification code has been sent to your email.',
        });
      }
    } catch (err) {
      console.error('Failed to resend code:', err);
      if (formikRef.current) {
        formikRef.current.setStatus({
          error: 'Failed to send new code. Please try again.',
        });
      }
    }
  };

  return (
    <div className="prelogin-container w-full">
      <div className="flex flex-col lg:flex-row min-h-screen">
        <div className="w-full lg:w-1/2 p-0">
          <div className="prelogin-leftside">
            <div className="container fix-height">
              <img className="logo logo-large" src={logoWhite} alt="logo white" />
              <div className="text">
                <h1 className="tagline-large">
                  Empowering healthcare providers with real-time prescription
                  coverage clarity
                </h1>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-1/2 p-0 bg-white">
          <div className="prelogin-rightside">
            <div className="p-5">
              <div className="flex justify-between mb-8">
                <Link to="/reset-password" onClick={onBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors no-underline">
                  <IconChevronLeft size={20} /> Back
                </Link>
              </div>

              <Formik
                innerRef={formikRef}
                initialValues={{
                  code: '',
                  newPassword: '',
                  confirmPassword: '',
                  showPassword: false,
                }}
                validationSchema={ConfirmResetSchema}
                onSubmit={handleConfirmation}
              >
                {({
                  errors,
                  touched,
                  status,
                  isSubmitting,
                  values,
                  setFieldValue,
                }) => (
                  <Form className="cst-form">
                    <h1 className="text-2xl font-bold mb-2 text-center">
                      Create New Password
                    </h1>
                    <p className="text-muted-foreground text-sm mb-4 text-center">
                      Enter the verification code sent to your email and create
                      your new password.
                    </p>

                    <div className="mb-4">
                      <label htmlFor="code" className="block pb-2 text-sm font-semibold">
                        Verification Code
                      </label>
                      <Field
                        type="text"
                        className={`form-control ${touched.code && errors.code ? 'border-red-500' : ''}`}
                        id="code"
                        name="code"
                        placeholder="Enter 6-digit code"
                        maxLength="6"
                      />
                      {touched.code && errors.code && (
                        <div className="text-red-500 text-sm mt-1">{errors.code}</div>
                      )}
                    </div>

                    <div className="mb-4">
                      <label
                        htmlFor="newPassword"
                        className="block pb-2 text-sm font-semibold"
                      >
                        New Password
                      </label>
                      <div className="input-with-icon create-pass">
                        <Field
                          type={values.showPassword ? 'text' : 'password'}
                          className={`form-control pr-12 ${touched.newPassword && errors.newPassword ? 'border-red-500' : ''}`}
                          id="newPassword"
                          name="newPassword"
                          placeholder="Enter new password"
                        />
                        {values.newPassword && (
                          <span
                            className="password-toggle-icon"
                            onClick={() =>
                              setFieldValue('showPassword', !values.showPassword)
                            }
                            style={{ cursor: 'pointer' }}
                          >
                            {values.showPassword ? (
                              <IconEyeOff size={20} />
                            ) : (
                              <IconEye size={20} />
                            )}
                          </span>
                        )}
                      </div>
                      {values.newPassword && (
                        <PasswordRequirements 
                          password={values.newPassword} 
                          className="mt-3"
                        />
                      )}
                      {touched.newPassword && errors.newPassword && (
                        <div className="text-red-500 text-sm mt-1">
                          {errors.newPassword}
                        </div>
                      )}
                    </div>

                    <div className="mb-4">
                      <label
                        htmlFor="confirmPassword"
                        className="block pb-2 text-sm font-semibold"
                      >
                        Confirm New Password
                      </label>
                      <div className="input-with-icon create-pass">
                        <Field
                          type={values.showConfirmPassword ? 'text' : 'password'}
                          className={`form-control pr-12 ${touched.confirmPassword && errors.confirmPassword ? 'border-red-500' : ''}`}
                          id="confirmPassword"
                          name="confirmPassword"
                          placeholder="Re-enter new password"
                        />
                        {values.confirmPassword && (
                          <span
                            className="password-toggle-icon"
                            onClick={() =>
                              setFieldValue(
                                'showConfirmPassword',
                                !values.showConfirmPassword
                              )
                            }
                            style={{ cursor: 'pointer' }}
                          >
                            {values.showConfirmPassword ? (
                              <IconEyeOff size={20} />
                            ) : (
                              <IconEye size={20} />
                            )}
                          </span>
                        )}
                      </div>
                      {touched.confirmPassword && errors.confirmPassword && (
                        <div className="text-red-500 text-sm mt-1">
                          {errors.confirmPassword}
                        </div>
                      )}
                    </div>

                    {status && (status.error || status.success) && (
                      <div
                        className={`rounded-md p-3 mb-4 flex items-center gap-2 ${status.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}
                        role="alert"
                      >
                        <IconAlertSquareRounded size={20} />
                        <span>{status.error || status.success}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-2.5 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span 
                            className="spinner-border spinner-border-sm me-2" 
                            role="status" 
                            aria-hidden="true"
                          />
                          <span>Resetting Password...</span>
                        </>
                      ) : (
                        'Reset Password'
                      )}
                    </button>

                    <div className="text-center mt-3">
                      <p className="text-sm mb-2">Didn&apos;t receive the code?</p>
                      <button
                        type="button"
                        className="text-primary hover:underline font-medium text-sm"
                        onClick={handleResendCode}
                        disabled={resendCooldown > 0}
                      >
                        {resendCooldown > 0
                          ? `Resend code in ${resendCooldown}s`
                          : 'Resend code'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ConfirmResetPassword.propTypes = {
  username: PropTypes.string.isRequired,
  onBack: PropTypes.func,
};

export default ConfirmResetPassword;
